import React, { useEffect, useState } from "react";

//** ndbx */
import {
  Button,
  ButtonStack,
  NotificationCard,
  Section,
} from "@arp/react-ndbx";

//** MUI Components */
import { Box, Divider } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import useTheme from "@mui/material/styles/useTheme";

//** App Components */
import Page from "../../../components/Page.jsx";
import QuoteSummaryDetails from "../../../components/pageLevel/quoteSummary/QuoteSummaryDetails.jsx";
import QuoteSummaryHeader from "../../../components/pageLevel/quoteSummary/QuoteSummaryHeader.jsx";

//** Context */
import { useOutletContext } from "react-router-dom";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../../hooks/useSharedData.ts";
// import { useCreateQuote } from "../../hooks/dataHooks/useCreateQuote.ts";

//** Hooks */
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData.js";

//** Utilities */
import ButtonNavigationGroup from "../../../components/organisms/ButtonNavigationGroup.jsx";
import OtpVerificationModal from "../../../components/pageLevel/otpVerificationModal/OtpVerificationModal.jsx";
import SendEmailModal from "../../../components/pageLevel/sendEmailModal/SendEmailModal.jsx";
import {
  Application,
  TRACKIFY_EVENT,
  sendEmailModalContent,
} from "../../../config/const/enums.js";
import { retrieveQuoteNotification } from "../../../config/disclaimers/disclaimers.jsx";
import FadeInOut from "../../../utilities/fadeInOut";
import { pushEvent, trackifyPageChange } from "@allianz/tracking";

//----------------------------------------------------------------
export default function QuoteSummary() {
  //** Toggle for OTP verification */
  const skipOtp = "true" === import.meta.env.VITE_SKIP_OTP_VERIFICATION;

  const {
    showSecond: [showSecond],
    isDirty: [isDirty],
    isValid: [isValid],
    errors: [errors],
  } = useOutletContext();

  const { state } = usePreQualificationPages();
  const { currentPage } = { ...state };
  const { dataSet } = usePreQualification();
  const isOnline = dataSet?.application === Application.ONLINE;

  // const { personOne, personTwo } = { ...dataSet };
  const theme = useTheme();

  const { handleEventChange, retrieveQuoteJourney } = useSharedData();

  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const [openOtpVerification, setOpenOtpVerification] = useState(false);

  const handleSendEmailOpen = () => {
    trackifyPageChange(TRACKIFY_EVENT.SEND_EMAIL_POLICY_SUMMARY);
    setOpenSendEmailModal(true);
  };
  const handleSendEmailClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenSendEmailModal(false);
      setSendQuoteEmailSuccess(false);
    }
  };
  const [sendQuoteEmailSuccess, setSendQuoteEmailSuccess] = useState(false);
  const [otpSendSuccess, setOtpSendSuccess] = useState(false);
  const handleSuccessEmailModal = () => {
    setSendQuoteEmailSuccess(true);
  };

  const handleOtpVerification = () => {
    trackifyPageChange(TRACKIFY_EVENT.SEND_OTP_MODAL);
    setOpenOtpVerification(true);
  };
  const handleOtpVerificationClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenOtpVerification(false);
      setOtpSendSuccess(false);
    }
  };
  const verifyOtpNumberModal = () => {
    setOtpSendSuccess(true);
  };

  const [frequency, setFrequency] = useState("monthly");
  const handleOnChange = (e) => {
    if (!e) return;
    setFrequency(e);
  };

  function anyErrors() {
    const foundErrors = Object?.keys(errors).length > 0 ? true : false;
    return foundErrors;
  }

  const { isLoading, isFetching, data, error } = useGetQuote();

  useEffect(() => {
    if (isLoading === true || data == null) return;

    if (data?.quoteInformation?.jwtToken != null) {
      handleEventChange({
        name: "accessToken",
        value: data?.quoteInformation?.jwtToken,
      });

      //* Only push the event when we have a quoteNumber
      if (data?.quoteInformation?.quoteNumber !== undefined) {
        pushEvent("quoteNumber", data?.quoteInformation?.quoteNumber);
      }
    }
  }, [data]);

  useEffect(() => {
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
  }, []);

  if (isLoading || isFetching) {
    return <div>CREATING QUOTE.....</div>;
  }

  const { numberOfApplicants, personOneQuoteInfo, personTwoQuoteInfo } = {
    ...data,
  };

  const dataAvailable = isLoading && isFetching;

  //----------------------------------------------------------------

  return (
    <>
      {data ? (
        <Section sx={{ minWidth: `calc(100vw -  50px)` }}>
          <QuoteSummaryHeader
            isOnline={isOnline}
            onChange={(e) => handleOnChange(e)}
          />
        </Section>
      ) : null}
      <Page title={currentPage.title} identifier={currentPage.identifier}>
        <Backdrop
          sx={{ color: "#008ED6", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={dataAvailable}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        {/* First Applicant Details*/}
        {data && !dataAvailable ? (
          <>
            {/* show the notification in case of retrieve journey via CC */}
            {retrieveQuoteJourney && (
              <Section md={7} mb={4}>
                <FadeInOut
                  show={1 === 1}
                  duration={500}
                  style={{ width: "100%" }}
                >
                  <Box>
                    <NotificationCard
                      cardType="info"
                      title="Application retrieval important information"
                      message={
                        isOnline
                          ? retrieveQuoteNotification()
                              .DISCLAIMER_TEXT_REVIEW_ONLINE
                          : retrieveQuoteNotification().DISCLAIMER_TEXT_REVIEW_CC(
                              dataSet["personOne"].emailAddress,
                              dataSet["personOne"].contactNumber
                            )
                      }
                    />
                  </Box>
                </FadeInOut>
              </Section>
            )}
            <FadeInOut show={1 === 1} duration={500} style={{ width: "100%" }}>
              <QuoteSummaryDetails
                person="personOne"
                frequency={frequency}
                applicantTitle="1"
                dataSet={dataSet}
                isOnline={isOnline}
              />
            </FadeInOut>
          </>
        ) : (
          <FadeInOut show={1 === 1} duration={500} style={{ width: "100%" }}>
            <Skeleton
              sx={{ bgcolor: "#F1F9FA" }}
              animation="wave"
              variant="rounded"
              width={900}
              height={"75vh"}
            />
          </FadeInOut>
        )}
        <Divider />

        {/* ----------------------------------------------------------------*/}

        {/* Second Applicant Details*/}
        {numberOfApplicants === 2 ? (
          <>
            {data && !dataAvailable ? (
              <FadeInOut
                show={1 === 1}
                duration={500}
                style={{ width: "100%" }}
              >
                <QuoteSummaryDetails
                  person="personTwo"
                  frequency={frequency}
                  applicantTitle="2"
                  dataSet={dataSet}
                  quoteInfo={personTwoQuoteInfo}
                  isOnline={isOnline}
                />
              </FadeInOut>
            ) : null}
          </>
        ) : null}
        {/* Navigation Buttons  */}
        <Section mt={6}>
          <ButtonStack>
            <Button
              variant="contained"
              color="secondary"
              disabled={!isDirty || !isValid || isLoading}
              onClick={handleSendEmailOpen}
            >
              SAVE AND EMAIL QUOTE
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!isDirty || !isValid || isLoading}
              // type="submit"
              type={
                !isOnline
                  ? "submit"
                  : retrieveQuoteJourney
                    ? "submit"
                    : skipOtp
                      ? "submit"
                      : "button"
              }
              onClick={
                !isOnline
                  ? null
                  : retrieveQuoteJourney
                    ? null
                    : skipOtp
                      ? null
                      : handleOtpVerification
              }
            >
              Next
            </Button>
          </ButtonStack>
          <Section mt={2}>
            <ButtonNavigationGroup showNextButton={false} />
          </Section>
        </Section>
        <SendEmailModal
          openSendEmailModal={openSendEmailModal}
          handleSendEmailClose={handleSendEmailClose}
          handleSuccessEmailModal={handleSuccessEmailModal}
          sendQuoteEmailSuccess={sendQuoteEmailSuccess}
          sendEmailImg={sendEmailModalContent.SEND_EMAIL_IMAGE}
          quoteStageNew={true}
        />
        <OtpVerificationModal
          openOtpVerification={openOtpVerification}
          handleOtpVerificationClose={handleOtpVerificationClose}
          verifyOtpNumberModal={verifyOtpNumberModal}
          otpSendSuccess={otpSendSuccess}
        />
      </Page>
    </>
  );
}
